import React, { useEffect, useState } from 'react';
import { Compass, Lock, ArrowRight } from 'phosphor-react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { useOptionsEntitlements } from '../../context/OptionsEntitlementsContext';
import { useOptionsLocale } from '../../context/OptionsLanguageContext';
import api from '../../lib/api';

export const BtcOutlookTeaser = () => {
  const navigate = useNavigate();
  const { isPro, loading: entitlementsLoading, openUpgradeModal } = useOptionsEntitlements();
  const { isPortuguese } = useOptionsLocale();
  const [outlook, setOutlook] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (entitlementsLoading) return;
    if (!isPro) {
      setLoading(false);
      setOutlook(null);
      return;
    }
    setLoading(true);
    api.get('/options/btc-outlook')
      .then((res) => {
        setOutlook(res.data);
      })
      .catch((err) => {
        console.error('Error fetching outlook teaser:', err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [entitlementsLoading, isPro]);

  const handleNavigate = () => {
    if (!isPro) {
      openUpgradeModal('BTC_OUTLOOK');
      return;
    }
    navigate('/options/outlook');
  };

  const getBiasColor = (bias) => {
    if (!bias) return 'text-white/60';
    const b = bias.toUpperCase();
    if (b.includes('BULLISH')) return 'text-[#4ADE80]';
    if (b.includes('BEARISH')) return 'text-[#F87171]';
    return 'text-white/80';
  };

  const getDirectionArrow = (bias) => {
    if (!bias) return '→';
    const b = bias.toUpperCase();
    if (b === 'BULLISH') return '↑';
    if (b === 'SLIGHTLY_BULLISH') return '↗';
    if (b === 'BEARISH') return '↓';
    if (b === 'SLIGHTLY_BEARISH') return '↘';
    return '→';
  };

  const formattedBiasLabel = (bias) => {
    if (!bias) return '—';
    return bias.replace('_', ' ').toUpperCase();
  };

  return (
    <Card 
      onClick={handleNavigate}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') handleNavigate();
      }}
      role="button"
      tabIndex={0}
      className="border border-white/5 bg-zinc-950/40 backdrop-blur-md hover:bg-zinc-950/60 hover:border-white/10 transition-all duration-300 rounded-[20px] text-white shadow-none cursor-pointer group"
      data-testid="options-dashboard-btc-outlook-teaser"
    >
      <CardContent className="p-5 flex flex-col justify-between min-h-[120px] space-y-3">
        
        {/* Teaser Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-white/45">
            <Compass size={12} weight="bold" />
            <span>BTC OUTLOOK</span>
            {!isPro && <Lock size={10} className="text-[#A855F7] ml-0.5" weight="fill" />}
          </div>
          <span className="text-[10px] text-white/35 font-medium">
            30–60D
          </span>
        </div>

        {/* Teaser Body */}
        <div>
          {loading ? (
            <div className="h-6 w-32 bg-white/5 animate-pulse rounded" />
          ) : !isPro ? (
            /* FREE TEASER */
            <div className="space-y-1">
              <p className="text-lg font-black text-white/20 select-none tracking-widest font-mono">
                ••••••••
              </p>
              <p className="text-[11px] text-white/45">
                {isPortuguese ? 'Viés do Bitcoin · Próximos 30–60D' : 'Bitcoin Market Bias · Next 30–60D'}
              </p>
            </div>
          ) : outlook?.status === 'published' ? (
            /* PRO TEASER */
            <div className="space-y-1">
              <p className={`text-lg sm:text-xl font-black tracking-tight flex items-center gap-1.5 ${getBiasColor(outlook.current_bias)}`}>
                {formattedBiasLabel(outlook.current_bias)}
                <span className="text-base text-[#A855F7] font-extrabold">
                  {getDirectionArrow(outlook.current_bias)}
                </span>
              </p>
              <p className="text-[11px] text-white/45">
                {isPortuguese ? 'Viés de mercado consolidado' : 'Consolidated market bias'}
              </p>
            </div>
          ) : (
            /* UNPUBLISHED OR EMPTY */
            <div className="space-y-0.5">
              <p className="text-xs font-semibold text-white/40">
                {isPortuguese ? 'Aguardando publicação' : 'Outlook Pending'}
              </p>
              <p className="text-[10px] text-white/35">
                {isPortuguese ? 'Nova leitura em breve' : 'New reading coming soon'}
              </p>
            </div>
          )}
        </div>

        {/* Teaser CTA */}
        <div className="flex items-center gap-1 text-[11px] font-bold text-white/70 group-hover:text-[#00E676] transition-colors duration-200">
          <span>
            {!isPro 
              ? (isPortuguese ? 'Unlock Outlook' : 'Unlock Outlook')
              : (isPortuguese ? 'View Outlook' : 'View Outlook')}
          </span>
          <ArrowRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
        </div>

      </CardContent>
    </Card>
  );
};

export default BtcOutlookTeaser;
